<template>
    <div>
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-3xl font-bold text-gray-900 mb-2">
                    Registrants
                </h2>
                <p >
                    Registered users
                </p>
            </div>
            <div>
                <div class="flex items-center gap-3">
                    <!-- Search Filter -->
                    <div class="dropdown dropdown-end">
                        <d-btn flat size="sm">
                            <d-icon icon="filter" />
                        </d-btn>
                        <div tabindex="0" class="dropdown-content z-[1] card card-compact w-64 p-4 shadow bg-white">
                            <d-field label="Search by Name">
                                <d-input 
                                    v-model="filter" 
                                    placeholder="Type to search..."
                                    size="sm"
                                />
                            </d-field>
                        </div>
                    </div>
                    
                    <!-- Add User Button -->
                    <d-btn 
                        color="secondary"
                        @click="openUserModal()"
                    >
                        <d-icon icon="plus" />
                        Add User
                    </d-btn>
                </div>
            </div>
        </div>
        
        <!-- Users Table -->
        <d-table 
            class="bg-white shadow mt-10 mb-5" 
            :data="registrants" 
            :columns="columns"
            :loading="loading" 
            backend-search 
            backend-sorting 
            :default-sort="[pagination.sortField, pagination.sortOrder]" 
            striped 
            default-sort-direction="desc" 
            @sort="onSort"
        >
            <template v-slot="{ row }">
                <td>{{ row.firstName }}</td>
                <td>{{ row.lastName }}</td>
                <td>
                    <div class="flex items-center gap-2">
                        <span>{{ row.email }}</span>
                        <d-chip
                            v-if="emailVerificationEnabled && !row.emailConfirmed"
                            color="warning"
                            size="sm"
                        >
                            Unverified
                        </d-chip>
                    </div>
                </td>
                <td>{{ formatPhoneNumber(row.phoneNumber) || '-' }}</td>
                <td>
                    <d-chip 
                        :color="row.active ? 'success' : 'warning'"
                        size="sm"
                    >
                        {{ row.active ? 'Active' : 'Inactive' }}
                    </d-chip>
                </td>
                <td>
                    <div class="flex gap-2">
                        <d-btn
                            v-if="emailVerificationEnabled && !row.emailConfirmed"
                            size="sm"
                            flat
                            color="secondary"
                            :loading="resendingUserId === row.id"
                            :disabled="resendingUserId === row.id"
                            title="Resend verification email"
                            @click="resendVerificationEmail(row)"
                        >
                            <d-icon icon="envelope" />
                        </d-btn>
                        <d-btn
                            size="sm"
                            flat
                            color="primary"
                            @click="openUserModal(row)"
                        >
                            <d-icon icon="edit" />
                        </d-btn>
                        <d-btn
                            size="sm"
                            flat
                            color="danger"
                            @click="confirmDelete(row)"
                        >
                            <d-icon icon="trash" />
                        </d-btn>
                    </div>
                </td>
            </template>
        </d-table>

        <!-- Pagination -->
        <div class="flex justify-end mt-6">
            <d-pagination 
                :total-items="total" 
                :per-page="pagination.pageSize" 
                v-model:current="pagination.page" 
            />
        </div>

        <!-- User Modal -->
        <user-modal
            :active="userModalActive"
            :user="selectedUser"
            :email-verification-enabled="emailVerificationEnabled"
            @close="closeUserModal"
            @saved="handleUserSaved"
        />

        <!-- Delete Confirmation Modal -->
        <confirm-modal
            :active="deleteModalActive"
            :title="'Delete User'"
            :message="`Are you sure you want to delete <strong>${userToDelete?.email}</strong>?<br><br>This action cannot be undone.`"
            :confirm-text="'Delete User'"
            @close="closeDeleteModal"
            @confirm="handleDeleteConfirmed"
        />
    </div>
</template>

<script>
import axios from 'axios';
import UserModal from './components/UserModal.vue';
import ConfirmModal from './components/ConfirmModal.vue';
import { toast } from '@services/toast.service';
import { debounce } from '@utils/debounce';

export default {
  name: 'RegistrantListingPage',
  
  components: {
    UserModal,
    ConfirmModal,
  },

  data() {
    return {
      registrants: [],
      loading: false,
      filter: '',
      total: 0,
      userModalActive: false,
      selectedUser: null,
      deleteModalActive: false,
      userToDelete: null,
      resendingUserId: null,
      emailVerificationEnabled: false,
      pagination: {
        page: 1,
        pageSize: 20,
        sortField: 'firstName',
        sortOrder: 'asc',
      },
    };
  },

  computed: {
    columns() {
      return [
        { field: 'firstName', label: 'First Name', sortable: true },
        { field: 'lastName', label: 'Last Name', sortable: true },
        { field: 'email', label: 'Email', sortable: true },
        { field: 'phoneNumber', label: 'Phone #', sortable: true },
        { field: 'isActive', label: 'Status', sortable: true },
        { field: 'actions', label: 'Actions', sortable: false },
      ];
    },
  },

  watch: {
    pagination: {
      handler() {
        this.getUsersPaged();
      },
      deep: true,
    },
    filter() {
      this.pagination.page = 1; // Reset to first page when filtering
      this.debouncedSearch();
    },
  },

  created() {
    // Create debounced search function with 400ms delay
    this.debouncedSearch = debounce(() => {
      this.getUsersPaged();
    }, 400);
  },

  mounted() {
    this.loadOrgSettings();
    this.getUsersPaged();
  },

  methods: {
    async loadOrgSettings() {
      try {
        const { data } = await axios.get('/api/admin/org-settings');
        this.emailVerificationEnabled = !!data.emailVerificationEnabled;
      } catch (error) {
        console.error('Error loading org settings:', error);
      }
    },

    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return '';
      const cleaned = phoneNumber.replace(/\D/g, '');
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
      return phoneNumber;
    },

    async getUsersPaged() {
      const { page, sortField, sortOrder, pageSize } = this.pagination;
      const params = [
        `filter=${encodeURIComponent(this.filter)}`,
        `sortDir=${sortField} ${sortOrder}`,
        `currentPage=${page}`,
        `pageSize=${pageSize}`,
        'roleName=Registrant'
      ].join('&');
      
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/users/paged?${params}`);
        this.registrants = data.resultItems || [];
        this.total = data.totalRecords || 0;
      } catch (error) {
        console.error('Error loading users:', error);
        toast.error('Failed to load users');
        this.registrants = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    openUserModal(user = null) {
      this.selectedUser = user;
      this.userModalActive = true;
    },

    closeUserModal() {
      this.userModalActive = false;
      this.selectedUser = null;
    },

    handleUserSaved() {
      this.getUsersPaged();
      this.closeUserModal();
    },

    confirmDelete(user) {
      this.userToDelete = user;
      this.deleteModalActive = true;
    },

    closeDeleteModal() {
      this.deleteModalActive = false;
      this.userToDelete = null;
    },

    async handleDeleteConfirmed() {
      if (!this.userToDelete) return;
      
      try {
        await axios.delete(`/api/users/${this.userToDelete.id}`);
        await this.getUsersPaged();
        toast.success('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      } finally {
        this.closeDeleteModal();
      }
    },

    onSort(field, order) {
      this.pagination.sortField = field;
      this.pagination.sortOrder = order;
    },

    async resendVerificationEmail(user) {
      if (!user || this.resendingUserId) return;

      this.resendingUserId = user.id;
      try {
        const { data } = await axios.post(`/api/users/${user.id}/resend-confirmation-email`);
        toast.success(data?.message || 'Verification email sent successfully');
      } catch (error) {
        console.error('Error resending verification email:', error);
        const message = error?.response?.data?.message || 'Failed to send verification email';
        toast.error(message);
      } finally {
        this.resendingUserId = null;
      }
    },
  },
};
</script>